// Dashboard properties (the ⚙ tab): name, description, theme colour, refresh
// interval, and the read-only identifiers.

import { useProjectStore } from '@/store/projectStore'
import {
  DASHBOARD_THEME_COLORS, DASHBOARD_THEME_DEFAULT, normalizeDashboardThemeColor,
} from '@twasta/visual-engine'
import type { DashboardBody } from './types'
import { Sec } from './designerShared'

const CUSTOM = '__custom__'

export default function PropertiesPanel({ body, objectName, canEdit, onChange }: {
  body: DashboardBody
  objectName: string
  canEdit: boolean
  onChange: <K extends keyof DashboardBody>(field: K, value: DashboardBody[K]) => void
}) {
  const appColor = useProjectStore((s) => s.currentProject?.application.theme_color) || ''
  const current = normalizeDashboardThemeColor(body.THEME_COLOR)
  const isCustom = current.startsWith('#')
  const swatch = isCustom
    ? current
    : DASHBOARD_THEME_COLORS.find((c) => c.id === current)?.color || appColor || 'var(--color-accent)'
  const refresh = body.REFRESH_INTERVAL_SECONDS || 0
  return (
    <fieldset disabled={!canEdit} style={{ margin: 0, padding: 0, border: 'none', minWidth: 0 }}>
      <div className="dd-phead"><div className="t"><div className="dd-ptitle">Dashboard properties</div></div></div>
      <Sec>
        <label className="dd-lab" htmlFor="dd-d-name">Name</label>
        <input id="dd-d-name" className="dd-inp" value={body.DASHBOARD_NAME}
          onChange={(e) => onChange('DASHBOARD_NAME', e.target.value)} placeholder="Sales overview" />
        <label className="dd-lab" htmlFor="dd-d-desc" style={{ marginTop: 12 }}>Description</label>
        <textarea id="dd-d-desc" className="dd-inp" rows={3} value={body.DASHBOARD_DESCR}
          onChange={(e) => onChange('DASHBOARD_DESCR', e.target.value)} placeholder="Add a description" />
        <p className="dd-hint">Shown under the dashboard&apos;s name. Leave it empty and the line is left out.</p>
      </Sec>
      <Sec>
        <label className="dd-lab" htmlFor="dd-d-theme">Theme colour</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span aria-hidden="true" style={{
            flex: '0 0 40px', height: 40, borderRadius: 10, background: swatch,
            boxShadow: 'inset 0 0 0 1px rgba(16,24,40,.12)',
          }} />
          <select id="dd-d-theme" className="dd-inp" value={isCustom ? CUSTOM : current}
            onChange={(e) => onChange('THEME_COLOR', e.target.value === CUSTOM
              ? (isCustom ? current : (/^#/.test(appColor) ? appColor : '#1565c0'))
              : e.target.value)}>
            {DASHBOARD_THEME_COLORS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            <option value={CUSTOM}>Custom colour…</option>
          </select>
          {current !== DASHBOARD_THEME_DEFAULT && (
            <button type="button" className="dd-tb out" style={{ height: 40 }}
              onClick={() => onChange('THEME_COLOR', DASHBOARD_THEME_DEFAULT)} title="Use the application's colour">
              Reset
            </button>
          )}
        </div>
        {isCustom && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <input type="color" value={current} onChange={(e) => onChange('THEME_COLOR', e.target.value)}
              aria-label="Custom colour"
              style={{ width: 48, height: 40, border: '1px solid var(--color-border)', borderRadius: 10, padding: 3, background: 'var(--color-bg-secondary)' }} />
            <span className="dd-mono" style={{ fontSize: 13 }}>{current}</span>
          </div>
        )}
        <p className="dd-hint">Colours the run-time header, filter card, Apply button, chips and tile badges. Charts keep their own colours.</p>
      </Sec>
      <Sec>
        <label className="dd-lab" htmlFor="dd-d-ref">Refresh interval</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input id="dd-d-ref" className="dd-inp mono" type="number" min={0} step={1} value={refresh} style={{ maxWidth: 120 }}
            onChange={(e) => onChange('REFRESH_INTERVAL_SECONDS', Math.max(0, Math.round(Number(e.target.value) || 0)))} />
          <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>seconds</span>
        </div>
        <p className="dd-hint">
          {refresh > 0
            ? `The tiles reload every ${refresh < 60 ? `${refresh} seconds` : `${Math.round(refresh / 60)} minute${Math.round(refresh / 60) === 1 ? '' : 's'}`} while the dashboard is open.`
            : '0 = no automatic reload.'}
        </p>
      </Sec>
      <Sec>
        <div className="dd-g2">
          <div><div className="dd-lab soft">Object name</div><div className="dd-ro dd-mono">{objectName}</div></div>
          <div><div className="dd-lab soft">Dashboard ID</div><div className="dd-ro dd-mono">{body.DASHBOARD_ID}</div></div>
          <div><div className="dd-lab soft">Type</div><div className="dd-ro">{body.DASHBOARD_TYPE || 'S'} — Standard</div></div>
          <div />
          <div>
            <label className="dd-lab soft" htmlFor="dd-d-by">Created by</label>
            <input id="dd-d-by" className="dd-inp" value={body.CREATED_BY} onChange={(e) => onChange('CREATED_BY', e.target.value)} />
          </div>
          <div>
            <label className="dd-lab soft" htmlFor="dd-d-dt">Created date</label>
            <input id="dd-d-dt" className="dd-inp" type="date" value={body.CREATED_DATE} onChange={(e) => onChange('CREATED_DATE', e.target.value)} />
          </div>
        </div>
        <p className="dd-hint">The object name and ID follow the dashboard&apos;s file. Rename it from the Workbench.</p>
      </Sec>
    </fieldset>
  )
}
